import { ExclamationCircleOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, Modal, message } from 'antd';
import axios from 'axios';
import { Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import box from '../../assets/img/fi_box_blue.svg';
import CreateBrand from './CreateBrand';
import useBrandStore from './brandStore';
import './profilepage.less';

/** Нэг хуудсанд татах брэндийн тоо — карт цөөн талбартай тул томоор татаж болно */
const PAGE_SIZE = 60;

/** Монограммын өнгө — брэндийн нэрнээс тогтмол сонгогдоно (профайл дүрсний оронд) */
const MONOGRAM_TONES = 6;

const toneOf = (name = '') => {
  let sum = 0;
  for (let i = 0; i < name.length; i += 1) sum += name.charCodeAt(i);
  return sum % MONOGRAM_TONES;
};

const initialOf = (name = '') => (name.trim()[0] ?? '?').toUpperCase();

const BrandTab = () => {
  const isListChanged = useBrandStore((state) => state.isListChanged);

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isAdd, setIsAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchPage(1);
    //eslint-disable-next-line
  }, [isListChanged]);

  /** page === 1 бол жагсаалтыг шинэчилнэ, эс бөгөөс сүүлд нь залгана */
  const fetchPage = async (nextPage) => {
    if (nextPage === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const { data } = await axios.post('/api/1.0/productgroup/list', {
        page: nextPage,
        size: PAGE_SIZE,
      });
      const { docs = [], totalDocs = 0 } = data?.result ?? {};

      setBrands((prev) => (nextPage === 1 ? docs : [...prev, ...docs]));
      setTotal(totalDocs);
      setPage(nextPage);
    } catch (e) {
      console.error('Fetch Brands: ', e);
    }

    setLoading(false);
    setLoadingMore(false);
  };

  const deleteBrand = async (id) => {
    setDeletingId(id);
    try {
      const { data } = await axios.post('/api/1.0/productgroup/delete', { _id: id });
      if (data?.statusCode === 200) {
        message.success('Брэнд устгагдлаа');
        fetchPage(1);
      }
    } catch (e) {
      console.error('Delete Brand: ', e);
    }
    setDeletingId(null);
  };

  const confirmDelete = (brand) => {
    Modal.confirm({
      title: 'Брэнд устгах',
      icon: <ExclamationCircleOutlined />,
      content: `«${brand?.name}» брэндийг устгана. Энэ үйлдлийг буцаах боломжгүй.`,
      okText: 'Устгах',
      cancelText: 'Болих',
      okButtonProps: { danger: true },
      onOk: () => deleteBrand(brand?._id),
    });
  };

  const visibleBrands = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return brands;
    return brands.filter((brand) => brand?.name?.toLowerCase().includes(keyword));
  }, [brands, search]);

  const hasMore = brands.length < total;

  const renderBody = () => {
    if (loading) {
      return (
        <div className="brand-grid">
          {Array.from({ length: 6 }).map((_, index) => (
            <div className="brand-card brand-card--skeleton" key={index} />
          ))}
        </div>
      );
    }

    if (brands.length === 0) {
      return (
        <div className="brand-empty">
          <img src={box} alt="" height={56} />
          <p className="brand-empty__title">Брэнд бүртгэгдээгүй байна</p>
          <p className="brand-empty__hint">Бараагаа бүлэглэхийн тулд эхний брэндээ нэмнэ үү.</p>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAdd(true)}>
            Брэнд нэмэх
          </Button>
        </div>
      );
    }

    if (visibleBrands.length === 0) {
      return (
        <div className="brand-empty">
          <p className="brand-empty__title">«{search.trim()}» гэсэн брэнд олдсонгүй</p>
          <p className="brand-empty__hint">Өөр түлхүүр үгээр хайж үзнэ үү.</p>
          <Button onClick={() => setSearch('')}>Хайлт цэвэрлэх</Button>
        </div>
      );
    }

    return (
      <>
        <ul className="brand-grid">
          {visibleBrands.map((brand) => (
            <li className="brand-card" key={brand?._id}>
              <span className={`brand-card__monogram brand-card__monogram--${toneOf(brand?.name)}`}>
                {initialOf(brand?.name)}
              </span>
              <span className="brand-card__name" title={brand?.name}>
                {brand?.name}
              </span>
              <button
                type="button"
                className="brand-card__delete"
                aria-label={`${brand?.name} брэндийг устгах`}
                disabled={deletingId === brand?._id}
                onClick={() => confirmDelete(brand)}
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>

        {hasMore && !search && (
          <div className="brand-more">
            <Button loading={loadingMore} onClick={() => fetchPage(page + 1)}>
              Цааш үзэх
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="pp_table_wrapper">
      <div className="brand-header">
        <div>
          <h3 className="brand-header__title">
            Брэнд
            {!loading && brands.length > 0 && <span className="brand-header__count">{total}</span>}
          </h3>
          <p className="brand-header__hint">Барааг бүлэглэхэд ашиглагдах брэндүүд.</p>
        </div>

        <div className="brand-header__actions">
          {brands.length > 0 && (
            <Input
              allowClear
              className="brand-search"
              prefix={<SearchOutlined />}
              placeholder="Брэнд хайх"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAdd(true)}>
            Брэнд нэмэх
          </Button>
        </div>
      </div>

      {renderBody()}

      {isAdd && <CreateBrand visible={isAdd} onCancel={() => setIsAdd(false)} />}
    </div>
  );
};

export default BrandTab;
